/**
 * Headless domain helpers for the issue-authoring journeys — labels,
 * progress derivation, and formatting. No rendering, no styling.
 */

import { formatShortDateOrDash } from "./formatters";

export const ISSUE_STATUS_LABELS: Record<string, string> = {
  open: "Open",
  investigating: "Investigating",
  resolved: "Resolved",
};

export const ISSUE_TYPE_LABELS: Record<string, string> = {
  quality: "Quality issue",
  customer_complaint: "Customer complaint",
  delivery: "Delivery issue",
  production: "Production issue",
  improvement: "Improvement suggestion",
  internal_process: "Internal / process",
  feedback: "Feedback",
};

/** Issue types that are about a customer; the rest are internal. */
export const CUSTOMER_ISSUE_TYPES: ReadonlySet<string> = new Set<string>([
  "quality",
  "customer_complaint",
  "delivery",
  "production",
  "feedback",
]);

export function issueStatusLabel(status: string): string {
  return ISSUE_STATUS_LABELS[status] ?? status;
}

export function issueTypeLabel(type: string): string {
  return ISSUE_TYPE_LABELS[type] ?? type;
}

/** A resolved issue is one whose corrective action is done and signed off. */
export function isIssueResolved(issue: { status: string }): boolean {
  return issue.status === "resolved";
}

/** True once an issue has entered (or passed through) investigation. */
export function isIssueUnderInvestigation(issue: { status: string }): boolean {
  return issue.status === "investigating";
}

export function formatIssueAge(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

/** Minimal data needed to derive the three-step progress trail. */
export interface IssueProgressData {
  status: string;
  raised_at: string;
  raised_by_id?: string | null;
  assignee_id: string | null;
  resolved_at?: string | null;
  resolved_by_id?: string | null;
}

export interface IssueProgressStep {
  key: "raised" | "investigating" | "resolved";
  label: string;
  reached: boolean;
  current: boolean;
  /** True for the trail's terminal step. A terminal step that is also
   *  `current` has finished, not stalled — the rendered trail must not
   *  paint it as the in-progress state amber steps mean elsewhere. */
  terminal: boolean;
  detail: string | null;
}

/**
 * Derive the three-step progress trail shown to every tier (including
 * reporters) — workflow metadata only, never investigation content.
 */
export function deriveIssueProgress(
  issue: IssueProgressData,
): IssueProgressStep[] {
  const investigationReached =
    issue.status === "investigating" || issue.status === "resolved";
  const resolved = issue.status === "resolved";

  const investigatingDetail = issue.assignee_id ? "assigned" : null;
  const resolvedDetail = issue.resolved_at
    ? formatShortDate(issue.resolved_at)
    : null;

  return [
    {
      key: "raised",
      label: "Raised",
      reached: true,
      current: issue.status === "open",
      terminal: false,
      detail: formatShortDate(issue.raised_at),
    },
    {
      key: "investigating",
      label: "Investigating",
      reached: investigationReached,
      current: issue.status === "investigating",
      terminal: false,
      detail: investigatingDetail,
    },
    {
      key: "resolved",
      label: "Resolved",
      reached: resolved,
      current: resolved,
      terminal: true,
      detail: resolvedDetail,
    },
  ];
}

export function formatShortDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return formatShortDateOrDash(d);
}
